/**
 * geocodeZipCentroid
 *
 * Resolve a US ZIP code to its approximate centre so the manual address-entry
 * flow can still drop a pin (with a wide circle that reads "approximate") when
 * Google Places can't find a precise match.
 *
 * Asks the LOS backend — `GET /pricing/geocode_zip/{zip}`, which geocodes with
 * Azure Maps server-side → { zipCode, found, lat, lng, city, state, county,
 * formattedAddress, source }. Not Google from the browser: the Geocoding web
 * service refuses our referrer-restricted key ("REQUEST_DENIED: API keys with
 * referer restrictions cannot be used with this API"), so every ZIP used to
 * fail (Request Board REQ-118). The backend route exists for exactly that.
 *
 * The route returns a point, not the ZIP's bounds, so the radius is a fixed
 * 5 miles (inside PropertyMap's 1–20 mile radiusOptions).
 *
 * Returns null when the ZIP doesn't resolve or the call fails — the caller
 * shows "Could not look up that ZIP".
 */
import { losHttp } from "../../lib/losHttp";

const DEFAULT_RADIUS_MILES = 5;

export const geocodeZipCentroid = async (zip) => {
  if (!zip) return null;
  const cleaned = String(zip).trim().replace(/\s+/g, "");
  if (!/^\d{5}(-\d{4})?$/.test(cleaned)) return null;
  const fiveDigit = cleaned.slice(0, 5);

  let data;
  try {
    const res = await losHttp.get(`/pricing/geocode_zip/${encodeURIComponent(fiveDigit)}`, {
      logName: "pricing.geocode_zip",
    });
    data = res.data;
  } catch (err) {
    console.error("[geocodeZipCentroid] lookup failed:", err);
    return null;
  }

  if (!data?.found || data.lat == null || data.lng == null) return null;

  return {
    latitude: Number(data.lat),
    longitude: Number(data.lng),
    radiusMiles: DEFAULT_RADIUS_MILES,
    formattedAddress: data.formattedAddress || null,
    city: data.city || null,
    state: data.state || null,
    county: data.county || null,
  };
};
